/**
 * StabSkillEffect — Blades tree foundation active: quick directional thrust.
 *
 * Short-range single-target melee attack in the player's facing direction.
 * Breaks Hide on activation. Fast cooldown. Supports upgrade keys:
 * - Clean Entry: `damage` (multiplicative), `recoveryMs` (additive, negative)
 * - Weak Point: `staggerChance` + `staggerDurationMs` (both additive)
 *
 * Implements SkillEffect directly with its own cooldown management
 * (InstantSkillEffect has no cooldown). Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Stab
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface StabDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startAttack(attackType: string): void;
  };
  readonly concealment: {
    isConcealed(): boolean;
    breakHide(): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface StabConfig {
  readonly cooldownMs: number;
  readonly baseDamageMultiplier: number;
}

const DEFAULT_CONFIG: StabConfig = {
  cooldownMs: 400,                  // fast cooldown tier
  baseDamageMultiplier: 1.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class StabSkillEffect implements SkillEffect {
  readonly skillId = 'stab';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: StabDeps;
  private readonly config: StabConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _hidesBroken = 0;
  private _resolvedDamageMultiplier = 1.0;
  private _resolvedCooldownMs = 0;
  private _wasConcealed = false;

  constructor(deps: StabDeps, config: Partial<StabConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get attacksFired(): number { return this._attacksFired; }
  get hidesBroken(): number { return this._hidesBroken; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }
  get resolvedCooldownMs(): number { return this._resolvedCooldownMs; }
  get wasConcealed(): boolean { return this._wasConcealed; }

  get cooldown(): number { return this._cooldownRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve damage multiplier (base × Clean Entry)
    this._resolvedDamageMultiplier = this.config.baseDamageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('stab', 'damage');

    // Resolve cooldown (base + Clean Entry recovery reduction)
    const recoveryBonus = this.deps.resolver.getAdditiveEffect('stab', 'recoveryMs');
    this._resolvedCooldownMs = Math.max(0, this.config.cooldownMs + recoveryBonus);

    // Check and break Hide
    this._wasConcealed = this.deps.concealment.isConcealed();
    if (this._wasConcealed) {
      this.deps.concealment.breakHide();
      this._hidesBroken++;
    }

    // Fire the attack
    this.deps.combat.startAttack('stab');
    this._attacksFired++;

    // Start cooldown
    this._cooldownRemaining = this._resolvedCooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
